import { Divider } from 'antd';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Category } from 'api/timesheets/categories';
import { Configuration } from 'api/timesheets/configuration/configuration';
import { TimesheetReportsApi } from 'api/timesheets/reports/api';
import {
  TimesheetReportSummary,
  TimesheetReportType,
} from 'api/timesheets/reports/types';
import { Switcher } from 'components/atoms/switcher';
import { TableSkeleton } from 'components/atoms/table';
import {
  DateRange,
  getFormattedValueFromDateRange,
} from 'components/organisms/projects-report/common/range-picker/types';
import { FormatTime } from 'components/organisms/timesheets/common/format-time';
import {
  BillabilitySummary,
  HeaderSummary,
  MetricSummary,
  Wrapper,
} from 'components/organisms/timesheets/common/style';
import { TimesheetTableWithSummary } from '../common';
import {
  useCategoryWisePeopleConfig,
  useCategoryWiseProjectConfig,
} from '../table-config';
import { categoryWiseReportTabs } from '../types';
import { useColumnConfig } from '../use-column-config';
import { useReports } from '../use-reports';
import { useSummaryDetails } from '../use-summary-detail';
import { useTab } from '../use-tab';

const TabRow = styled.div`
  padding: var(--spacing-05);
`;
const TableWrapper = styled.div`
  flex: 1;
  height: 100%;
  margin: 0 var(--spacing-05) var(--spacing-05);
`;

interface Props {
  summary: TimesheetReportSummary;
  timesheetCategories: Category[];
  dateRange: DateRange;
  configuration: Configuration;
  category?: Category;
}

export const TimesheetCategoryWiseReport: FC<Props> = ({
  summary,
  timesheetCategories,
  dateRange,
  category,
  configuration,
}) => {
  const { selectedTab, setSelectedTab } = useTab(categoryWiseReportTabs);
  const { totalMinutes, billableMinutes, nonBillableMinutes } =
    useSummaryDetails({
      summary,
      dateRange,
      timesheetCategories,
      configuration,
    });

  const reportApi = useCallback(() => {
    const [startDate, endDate] = getFormattedValueFromDateRange(dateRange);

    return TimesheetReportsApi.getCategoryWiseReports(
      startDate,
      endDate,
      selectedTab as unknown as TimesheetReportType,
      category ? category.id : null,
    );
  }, [dateRange, selectedTab, category]);

  const { isReportsLoading, timesheetReports, timesheetReportsSummary } =
    useReports(reportApi);

  const projectColumnConfig = useCategoryWiseProjectConfig({
    noOfRows: timesheetReports.length,
  });

  const peopleColumnConfig = useCategoryWisePeopleConfig({
    noOfRows: timesheetReports.length,
  });

  const columnConfig = useColumnConfig({
    selectedTab,
    projectColumnConfig,
    peopleColumnConfig,
    categoryColumnConfig: null,
    taskColumnConfig: null,
    phaseColumnConfig: null,
  });

  return (
    <Wrapper>
      <HeaderSummary>
        <BillabilitySummary
          billableMinutes={billableMinutes}
          nonBillableMinutes={nonBillableMinutes}
        />
        <MetricSummary
          title={
            <FormattedMessage id="timesheets.summary.total_time_tracked" />
          }
          value={<FormatTime value={totalMinutes} showHypenIfZero={false} />}
        />
      </HeaderSummary>
      <Divider style={{ margin: 0 }} />
      <TabRow>
        <Switcher
          value={selectedTab}
          onChange={(e) => {
            setSelectedTab(e.target.value);
          }}
        >
          {categoryWiseReportTabs.map((tab) => (
            <Switcher.Button key={tab} value={tab}>
              <FormattedMessage id={`timesheets.reports.tabs.${tab}`} />
            </Switcher.Button>
          ))}
        </Switcher>
      </TabRow>
      <TableWrapper>
        {!isReportsLoading ? (
          <TimesheetTableWithSummary
            columns={columnConfig}
            dataSource={timesheetReports}
            summaryDataSource={[timesheetReportsSummary]}
          />
        ) : (
          <TableSkeleton columnCount={8} />
        )}
      </TableWrapper>
    </Wrapper>
  );
};
